import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, where, onSnapshot, doc, writeBatch, arrayUnion, orderBy } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import ExpenseDetailModal from '../../expenses/ExpenseDetailModal';
import ExpenseRejectModal from './ExpenseRejectModal';
import ExpenseCategoryManager from './ExpenseCategoryManager';
import ExpenseStatements from './ExpenseStatements';
import { EyeIcon, CheckCircleIcon, XCircleIcon, DownloadIcon } from '../../../components/icons/Icons';
import AllReportsView from './AllReportsView';

// ############################################################################
// ## NEW: Reimbursement History View with Pagination
// ############################################################################
function ReimbursementHistoryView({ appId, userProfile, onViewReport }) {
    const [historyReports, setHistoryReports] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'updatedAt', direction: 'descending' });
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    useEffect(() => {
        if (!appId) {
            setIsLoading(false);
            return;
        }
        const reportsCollectionPath = `/artifacts/${appId}/public/data/expenseReports`;
        const q = query(
            collection(db, reportsCollectionPath),
            where("status", "in", ["Reimbursed", "Rejected by Finance"]),
            orderBy("updatedAt", "desc")
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const fetchedReports = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            setHistoryReports(fetchedReports);
            setIsLoading(false);
        });

        return () => unsubscribe();
    }, [appId]);
    
    const processedHistory = useMemo(() => {
        let filtered = [...historyReports];
        if (searchTerm) {
            const lowercasedTerm = searchTerm.toLowerCase();
            filtered = filtered.filter(report =>
                report.reportName?.toLowerCase().includes(lowercasedTerm) ||
                report.reportNumber?.toLowerCase().includes(lowercasedTerm) ||
                report.requesterName?.toLowerCase().includes(lowercasedTerm)
            );
        }
        filtered.sort((a, b) => {
            let aValue = a[sortConfig.key];
            let bValue = b[sortConfig.key];
            if (aValue?.toDate) aValue = aValue.toDate();
            if (bValue?.toDate) bValue = bValue.toDate();
            if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
            return 0;
        });
        return filtered;
    }, [historyReports, searchTerm, sortConfig]);

    const paginatedHistory = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return processedHistory.slice(startIndex, startIndex + itemsPerPage);
    }, [processedHistory, currentPage, itemsPerPage]);

    const totalPages = Math.ceil(processedHistory.length / itemsPerPage);

    const handlePageChange = (newPage) => {
        if (newPage > 0 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const handleItemsPerPageChange = (event) => {
        setItemsPerPage(Number(event.target.value));
        setCurrentPage(1);
    };

    const requestSort = (key) => {
        const direction = sortConfig.key === key && sortConfig.direction === 'ascending' ? 'descending' : 'ascending';
        setSortConfig({ key, direction });
        setCurrentPage(1);
    };

    const getStatusBadgeColor = (status) => {
        if (status === 'Reimbursed') return 'bg-blue-100 text-blue-800';
        if (status === 'Rejected by Finance') return 'bg-red-100 text-red-800';
        return 'bg-gray-100 text-gray-800';
    };

    if (isLoading) return <LoadingSpinner />;

    return (
        <div>
            <div className="mb-4">
                <input
                    type="text"
                    placeholder="Search by report name, ID, or submitter..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-lg shadow-sm"
                />
            </div>
            <div className="bg-white shadow-md rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th onClick={() => requestSort('reportNumber')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Report ID</th>
                                <th onClick={() => requestSort('requesterName')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Submitter</th>
                                <th onClick={() => requestSort('totalAmount')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Amount</th>
                                <th onClick={() => requestSort('status')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Status</th>
                                <th onClick={() => requestSort('updatedAt')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Last Updated</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {paginatedHistory.map(report => (
                                <tr key={report.id}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono">{report.reportNumber}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">{report.requesterName}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">{report.reimbursementCurrency} {report.totalAmount.toFixed(2)}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeColor(report.status)}`}>
                                            {report.status}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">{report.updatedAt?.toDate().toLocaleDateString()}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                        <button onClick={() => onViewReport(report)} className="text-gray-500 hover:text-blue-600" title="View Details"><EyeIcon /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            {totalPages > 1 && (
                <div className="flex justify-between items-center mt-4">
                    <div className="flex items-center space-x-2 text-sm">
                        <span>Items per page:</span>
                        <select value={itemsPerPage} onChange={handleItemsPerPageChange} className="border border-gray-300 rounded-md p-1">
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                    </div>
                    <div className="flex items-center space-x-2">
                        <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="px-3 py-1 border rounded-md disabled:opacity-50">
                            Previous
                        </button>
                        <span className="text-sm">Page {currentPage} of {totalPages}</span>
                        <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="px-3 py-1 border rounded-md disabled:opacity-50">
                            Next
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}


// ############################################################################
// ## Main ExpenseCenter Component
// ############################################################################
export default function ExpenseCenter({ appId, userProfile }) {
    const [activeTab, setActiveTab] = useState('pending');
    const [isLoading, setIsLoading] = useState(true);
    const [pendingReports, setPendingReports] = useState([]);
    const [pendingCount, setPendingCount] = useState(0);
    const [allReportsCount, setAllReportsCount] = useState(0);
    const [selectedReport, setSelectedReport] = useState(null);
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);
    const [sortConfig, setSortConfig] = useState({ key: 'updatedAt', direction: 'descending' });
// State to hold the user/approver map
    const [approversMap, setApproversMap] = useState(new Map());

    // Effect to fetch all users for the approver map (needed by child components)
    useEffect(() => {
        const usersQuery = query(collection(db, `/artifacts/${appId}/users`));
        const unsub = onSnapshot(usersQuery, (snapshot) => {
            const newMap = new Map();
            snapshot.forEach(doc => {
                newMap.set(doc.id, doc.data().name); // Map ID to Name
            });
            setApproversMap(newMap);
        });
        return () => unsub();
    }, [appId]);

    // Effect to get the count of pending reports for the tab display
    useEffect(() => {
        if (!appId) return;
        const reportsCollectionPath = `/artifacts/${appId}/public/data/expenseReports`;
        const q = query(collection(db, reportsCollectionPath), where("status", "==", "Approved"));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setPendingCount(snapshot.size);
        });
        return () => unsubscribe();
    }, [appId]);

// --- FIX #2: Add a new effect to get the total count of ALL reports ---
    useEffect(() => {
        if (!appId) return;
        const reportsCollectionPath = `/artifacts/${appId}/public/data/expenseReports`;
        // This query has no filters, so it gets all reports
        const q = query(collection(db, reportsCollectionPath)); 
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setAllReportsCount(snapshot.size);
        });
        return () => unsubscribe();
    }, [appId]);

    // Effect to fetch ONLY pending reports
    useEffect(() => {
        if (!appId || activeTab !== 'pending') {
            setIsLoading(false);
            setPendingReports([]);
            return;
        }

        setIsLoading(true);
        const reportsCollectionPath = `/artifacts/${appId}/public/data/expenseReports`;
        const q = query(
            collection(db, reportsCollectionPath),
            where("status", "in", ["Approved"]),
            orderBy("updatedAt", "desc")
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const fetchedReports = snapshot.docs.map(d => ({ id: d.id, ref: d.ref, ...d.data() }));
            setPendingReports(fetchedReports);
            setIsLoading(false);
        }, (error) => {
            console.error("Error fetching pending reports:", error);
            setIsLoading(false);
        });

        return () => unsubscribe();
    }, [activeTab, appId]);

    const sortedPendingReports = useMemo(() => {
        let sortableItems = [...pendingReports];
        if (sortConfig.key) {
            sortableItems.sort((a, b) => {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];
                if (aValue?.toDate) aValue = aValue.toDate();
                if (bValue?.toDate) bValue = bValue.toDate();
                if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
                if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
                return 0;
            });
        }
        return sortableItems;
    }, [pendingReports, sortConfig]);

    const createAuditEntry = (action) => ({ action, user: userProfile.name, timestamp: new Date() });
    const handleViewReport = (report) => {
        setSelectedReport(report);
        setIsDetailModalOpen(true);
    };
    const handleOpenRejectModal = (report) => {
        setSelectedReport(report);
        setIsRejectModalOpen(true);
    };
    const handleReject = async (reason) => {
        if (!selectedReport) return;
        const reportRef = doc(db, selectedReport.ref.path);
        await writeBatch(db).update(reportRef, { status: 'Rejected by Finance', financeRejectionReason: reason, updatedAt: new Date(), auditTrail: arrayUnion(createAuditEntry(`Rejected (Reason: ${reason})`)) }).commit();
        setIsRejectModalOpen(false);
        setSelectedReport(null);
    };
    const handleBulkReimburse = async () => {
        if (selectedIds.length === 0) return;
        const batch = writeBatch(db);
        const auditEntry = createAuditEntry('Reimbursed');
        selectedIds.forEach(id => {
            const reportData = pendingReports.find(p => p.id === id);
            const reportRef = doc(db, reportData.ref.path);
            batch.update(reportRef, { status: 'Reimbursed', reimbursementDate: new Date(), auditTrail: arrayUnion(auditEntry) });
        });
        await batch.commit();
        setSelectedIds([]);
    };
    const handleDownloadReport = () => {
        // This function can be enhanced to download pending or history reports based on tab
        const dataToExport = sortedPendingReports
            .filter(report => selectedIds.includes(report.id))
            .map(({ reportNumber, requesterName, entity, totalAmount, reimbursementCurrency, createdAt }) => ({
                ReportID: reportNumber, SubmittedBy: requesterName, BillingEntity: entity,
                Amount: totalAmount, Currency: reimbursementCurrency, SubmissionDate: createdAt.toDate().toLocaleDateString(),
            }));
        // (CSV download logic here...)
    };
    const handleSelectAll = (e) => setSelectedIds(e.target.checked ? sortedPendingReports.map(r => r.id) : []);
    const handleSelectOne = (e, id) => {
        if (e.target.checked) setSelectedIds(prev => [...prev, id]);
        else setSelectedIds(prev => prev.filter(selectedId => selectedId !== id));
    };
    const requestSort = (key) => {
        const direction = sortConfig.key === key && sortConfig.direction === 'ascending' ? 'descending' : 'ascending';
        setSortConfig({ key, direction });
    };

    const renderContent = () => {
	if (activeTab === 'allReports') {
            return <AllReportsView appId={appId} onViewReport={handleViewReport} approversMap={approversMap} />;
        }
        if (activeTab === 'history') {
            return <ReimbursementHistoryView appId={appId} userProfile={userProfile} onViewReport={handleViewReport} />;
        }
        if (activeTab === 'categories') {
            return <ExpenseCategoryManager appId={appId} />;
        }
        if (activeTab === 'statements') {
            return <ExpenseStatements appId={appId} />;
        }
        if (activeTab === 'pending') {
            if (isLoading) return <LoadingSpinner />;
            return (
                <>
                    {selectedIds.length > 0 && (
                        <div className="flex justify-end items-center mb-4 p-4 bg-gray-50 rounded-lg border space-x-4">
                            <button onClick={handleDownloadReport} className="flex items-center px-4 py-2 bg-green-600 text-white font-semibold rounded-lg shadow-md hover:bg-green-700">
                                <DownloadIcon className="h-5 w-5 mr-2" /> Download Report ({selectedIds.length})
                            </button>
                            <button onClick={handleBulkReimburse} className="px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg shadow-md hover:bg-blue-700">
                                Mark Selected as Reimbursed ({selectedIds.length})
                            </button>
                        </div>
                    )}
                    <div className="bg-white shadow-md rounded-lg overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3"><input type="checkbox" onChange={handleSelectAll} checked={selectedIds.length > 0 && selectedIds.length === pendingReports.length} /></th>
                                        <th onClick={() => requestSort('reportNumber')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Report ID</th>
                                        <th onClick={() => requestSort('requesterName')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Submitter</th>
                                        <th onClick={() => requestSort('entity')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Entity</th>
                                        <th onClick={() => requestSort('totalAmount')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Amount</th>
                                        <th onClick={() => requestSort('updatedAt')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Approval Date</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {sortedPendingReports.map(report => (
                                        <tr key={report.id} className={selectedIds.includes(report.id) ? 'bg-blue-50' : ''}>
                                            <td className="px-6 py-4"><input type="checkbox" onChange={(e) => handleSelectOne(e, report.id)} checked={selectedIds.includes(report.id)} /></td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-mono">{report.reportNumber}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm">{report.requesterName}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm">{report.entity}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm">{report.reimbursementCurrency} {report.totalAmount.toFixed(2)}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm">{report.updatedAt?.toDate().toLocaleDateString() || 'N/A'}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                <div className="flex items-center space-x-3">
                                                    <button onClick={() => handleViewReport(report)} className="text-gray-500 hover:text-blue-600" title="View Details"><EyeIcon /></button>
                                                    <button onClick={() => handleOpenRejectModal(report)} className="text-red-500 hover:text-red-700" title="Reject"><XCircleIcon /></button>
                                                    <button onClick={async () => {
                                                        const reportRef = doc(db, report.ref.path);
                                                        await writeBatch(db).update(reportRef, { status: 'Reimbursed', reimbursementDate: new Date(), auditTrail: arrayUnion(createAuditEntry('Reimbursed')) }).commit();
                                                    }} className="text-green-500 hover:text-green-700" title="Mark Reimbursed"><CheckCircleIcon /></button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            );
        }
    };

    return (
        <div>
            <div className="border-b border-gray-200 mb-4">
                <nav className="-mb-px flex space-x-8">
                    <button onClick={() => setActiveTab('pending')} className={`${activeTab === 'pending' ? 'border-[#20BEC6] text-[#20BEC6]' : 'border-transparent text-gray-500'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}>
                        Pending Reimbursement {pendingCount > 0 && `(${pendingCount})`}
                    </button>
                    <button onClick={() => setActiveTab('history')} className={`${activeTab === 'history' ? 'border-[#20BEC6] text-[#20BEC6]' : 'border-transparent text-gray-500'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}>
                        Reimbursement History
                    </button>
                    <button onClick={() => setActiveTab('categories')} className={`${activeTab === 'categories' ? 'border-[#20BEC6] text-[#20BEC6]' : 'border-transparent text-gray-500'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}>
                        Expense Categories
                    </button>
                    <button onClick={() => setActiveTab('statements')} className={`${activeTab === 'statements' ? 'border-[#20BEC6] text-[#20BEC6]' : 'border-transparent text-gray-500'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}>
                        Expense Statements
                    </button>
 		   <button onClick={() => setActiveTab('allReports')} className={`${activeTab === 'allReports' ? 'border-[#20BEC6] text-[#20BEC6]' : 'border-transparent text-gray-500'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}>
                        All Reports {allReportsCount > 0 && `(${allReportsCount})`}
                    </button>
                </nav>
            </div>

            {renderContent()}

            {isDetailModalOpen && <ExpenseDetailModal report={selectedReport} onClose={() => setIsDetailModalOpen(false)} appId={appId} />}
            {isRejectModalOpen && <ExpenseRejectModal report={selectedReport} onClose={() => setIsRejectModalOpen(false)} onReject={handleReject} />}
        </div>
    );
}